'use client';

import { Search } from '@/icons/Search';
import { fetchSearchSuggest, mapSearchSuggestToPage } from '@/lib/searchSuggest';
import { PriceData, ProductDetail } from '@/models/Product';
import { SearchResponse } from '@/models/Search';
import { useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useState } from 'react';
import OptimizedProductCard from './OptimizedProductCard';
import ProductSearchResult from './ProductSearchResult';
import ProductsListingSkeleton from './ProductsListingSkeleton';


export default function SearchResultsView({
  initialQ,
  initialResult,
  suggestedProducts,
  suggestedPrices,
}: {
  initialQ: string;
  initialResult: SearchResponse | undefined;
  suggestedProducts: ProductDetail[];
  suggestedPrices: PriceData;
}) {
  const urlQ = (useSearchParams().get('q') || '').trim();
  const staleServer = Boolean(urlQ) && urlQ !== initialQ;
  const [live, setLive] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useLayoutEffect(() => {
    if (urlQ) return;
    window.location.reload();
  }, [urlQ]);

  useEffect(() => {
    if (!urlQ || !staleServer) {
      setLive(null);
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    fetchSearchSuggest(urlQ, ac.signal, 60)
      .then((data) => {
        if (!ac.signal.aborted) setLive(mapSearchSuggestToPage(data));
      })
      .catch(() => {
        if (!ac.signal.aborted) setLive({ categories: [], products: [] });
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [urlQ, staleServer]);

  if (!urlQ) {
    return <ProductsListingSkeleton />;
  }

  const result = staleServer ? live : initialResult;
  const hasHits =
    (result?.products?.length ?? 0) > 0 || (result?.categories?.length ?? 0) > 0;
  const showSuggestions = !staleServer && !hasHits && suggestedProducts.length > 0;

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 justify-between max-w-7xl w-full items-center">
        <div className="flex flex-col justify-between items-center w-full">
          <div className="mb-2">{urlQ} - Arama Sonuçları</div>
        </div>
      </div>
      {loading || (staleServer && !live) ? (
        <ProductsListingSkeleton />
      ) : hasHits && result ? (
        <ProductSearchResult searchResult={result} searchKeyword={urlQ} />
      ) : (
        <div className="px-4 md:px-0 flex flex-col gap-10 max-w-7xl mx-auto mb-12 w-full">
          <div className="flex flex-col items-center gap-4 py-10">
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-hzc_beige_300">
              <Search className="text-4xl text-hzc_purple_300" />
            </div>
            <div className="flex flex-col items-center gap-1.5 text-center">
              <p className="text-lg sm:text-xl font-bold text-hzc_purple_500">
                &ldquo;{urlQ}&rdquo; için sonuç bulunamadı.
              </p>
              {showSuggestions ? (
                <p className="text-sm text-hzc_purple_300">
                  Beğenebileceğiniz ürünleri aşağıda bulabilirsiniz.
                </p>
              ) : null}
            </div>
          </div>
          {showSuggestions ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="hidden sm:block h-px flex-1 bg-hzc_beige_400" />
              </div>
              <div className="mx-auto grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 justify-center w-full">
                {suggestedProducts.map((product, index) => (
                  <OptimizedProductCard
                    key={product.product.id}
                    product={product.product}
                    price={suggestedPrices[product.product.id]}
                    priority={index < 6}
                    listId="search_suggestions"
                    listName="suggestions"
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
